// src/components/states/brandenburg.js
export const brandenburg = {
  slug: "brandenburg",
  name: "Brandenburg",
  hero: {
    eyebrow: "Bundesland-Übersicht",
    title: "Erdwärme in Brandenburg",
    subtitle:
      "Kurze Übersicht zu Förderung, Genehmigungen, Datengrundlage und typischen Untergrundthemen in Brandenburg – ideal für eine schnelle Standort-Vorprüfung.",
    infoBox:
      "In Brandenburg sind Genehmigung/Anzeige und die Datenlage sehr gut dokumentiert. Entscheidend ist v. a. Grundwasser/Schutzgebiet + die korrekte Anzeige nach Geologiedatengesetz.",
  },
  seo: {
    title:
      "Erdwärme in Brandenburg – Förderung, Genehmigung & Datengrundlage | Erdwärme-Experten",
    description:
      "Kurzübersicht Erdwärme Brandenburg: Förderung (KfW), Genehmigung/Anzeige (u. a. Geologiedatengesetz), Datengrundlage (Geothermieportal, Wärmeleitfähigkeit) und Untergrund/Grundwasser.",
    canonical: "https://www.erdwaerme-experten.de/bundeslaender/brandenburg",
  },

  facts: [
    {
      title: "Förderung (Bund)",
      icon: "BadgeCheck",
      color: "from-rose-600 to-rose-500",
      bullets: [
        "Heizungstausch-Zuschuss über KfW Programm 458 für Bestandsgebäude",
        "30 % Grundförderung; persönliche Boni können die Quote erhöhen",
        "Maximal 80 %; kein separater Effizienzbonus für Erdwärme",
      ],
      note:
        "Förderstand 21.07.2026. Die tatsächliche Förderfähigkeit hängt von Gebäude, Eigentum, Einkommen und weiteren Voraussetzungen ab.",
      link: {
        label: "KfW 458 (offiziell)",
        href: "https://www.kfw.de/inlandsfoerderung/Privatpersonen/Bestehende-Immobilie/F%C3%B6rderprodukte/Heizungsf%C3%B6rderung-f%C3%BCr-Privatpersonen-Wohngeb%C3%A4ude-%28458%29/",
      },
    },
    {
      title: "Genehmigung & Anzeigen",
      icon: "ShieldCheck",
      color: "from-slate-700 to-slate-600",
      bullets: [
        "Vor Erdwärmebohrungen wird geprüft, ob ein Eingriff ins Grundwasser vorliegt (wasserwirtschaftlich strenge Anforderungen).",
        "Wasserrecht wird in Brandenburg je nach Anlagenart (z. B. Kollektor vs. Sonde) unterschieden.",
        "Zusätzliche Pflichten/Regelwerke u. a.: Geologiedatengesetz (Anzeige), Bauordnung; teils Abgrenzung zum Bundesberggesetz.",
        "Geologische Untersuchungen müssen spätestens 2 Wochen vor Beginn beim LBGR angezeigt werden (online).",
      ],
      note:
        "Praktisch wichtig: Schutzgebiete + Grundwasser-Nähe früh prüfen; Anzeige-/Dokupflichten sauber abarbeiten.",
      link: {
        label: "Energieportal Brandenburg: Genehmigung (offiziell)",
        href: "https://energieportal-brandenburg.de/cms/inhalte/themen/geothermie/oberflaechennahe-geothermie/genehmigung",
      },
    },
    {
      title: "Datengrundlage (Brandenburg stark)",
      icon: "Layers",
      color: "from-indigo-700 to-indigo-600",
      bullets: [
        "Geothermieportal Brandenburg: Standortabfrage Bodengeothermie/Oberflächennahe Geothermie (inkl. Flächenbedarf Kollektoren).",
        "Karten/Datendienste zur mittleren Wärmeleitfähigkeit (auf BÜK300-Basis abgeleitet).",
        "Online-Anzeigeportal für Bohrungen/geologische Untersuchungen (GeolDG/BBergG) vereinfacht das Verfahren.",
      ],
      note:
        "Für QuickCheck/GeoReport ist Brandenburg sehr dankbar: gute öffentliche Daten + klare Verfahrensinfos.",
      link: {
        label: "Geothermieportal Brandenburg",
        href: "https://energieportal-brandenburg.de/cms/inhalte/tools/geothermieportal-brandenburg",
      },
    },
    {
      title: "Untergrund & Grundwasser",
      icon: "Droplets",
      color: "from-emerald-700 to-emerald-600",
      bullets: [
        "Oberflächennah oft Lockergesteine (Sande/Kiese) → Grundwasser kann sehr relevant sein.",
        "Schutzgebiete/Grundwasserbedingungen beeinflussen Machbarkeit und Auflagen häufig stärker als die Wärmepumpe selbst.",
        "Für Sonden ist Bohrqualität/Verfüllung entscheidend (Gewässerschutz).",
      ],
      note:
        "Für eine belastbare Vorabschätzung zählt die lokale Kombination aus Geologie, Grundwasser und Platz/Bohrlogistik.",
    },
  ],

  dataCards: [
    {
      title: "Welche Daten sind verfügbar?",
      icon: "Layers",
      desc:
        "Geothermieportal + Wärmeleitfähigkeitskarten (BÜK300-basiert) ermöglichen eine schnelle, standortbezogene Ersteinschätzung – besonders hilfreich für Vorprüfungen.",
    },
    {
      title: "Was ist häufig der Engpass?",
      icon: "Drill",
      desc:
        "Schutzgebiete/Grundwasser + formale Anzeige-/Genehmigungsschritte (inkl. GeolDG) und die praktische Bohrlogistik (Zufahrt/Leitungen/Platz).",
    },
    {
      title: "Wie nutzt euer GeoReport das?",
      icon: "FileText",
      desc:
        "Standortbezogene Auswertung verfügbarer Untergrunddaten + erste Dimensionierung (Sonden/Kollektor) und Kosten-/Förderlogik – als Entscheidungshilfe vor Angeboten.",
    },
  ],

  faq: [
    {
      q: "Welche Anzeigen sind in Brandenburg typisch wichtig?",
      a: "In Brandenburg müssen geologische Untersuchungen (inkl. Bohrungen) nach Geologiedatengesetz spätestens zwei Wochen vor Beginn beim LBGR angezeigt werden. Zusätzlich gelten je nach Vorhaben wasserrechtliche Anforderungen und ggf. bergrechtliche Abgrenzungen.",
      links: [
        {
          label: "LBGR: GeolDG FAQ (2 Wochen Frist)",
          href: "https://lbgr.brandenburg.de/lbgr/de/geologischer-dienst/geologiedatengesetz-haeufig-gestellte-fragen/",
        },
        {
          label: "Online-Anzeigeportal (LBGR)",
          href: "https://bohranzeige-brandenburg.de/",
        },
      ],
    },
    {
      q: "Gibt es in Brandenburg gute Karten zur Wärmeleitfähigkeit?",
      a: "Ja – es gibt Karten zur mittleren Wärmeleitfähigkeit auf Basis der BÜK300-Legendeneinheiten, die für eine erste Abschätzung sehr hilfreich sind.",
      links: [
        {
          label: "Wärmeleitfähigkeit (PDF/Karten)",
          href: "https://geo.brandenburg.de/karten/htdocs/21042020_W%C3%A4rmeleitf%C3%A4higkeit.pdf",
        },
      ],
    },
    {
      q: "Wo finde ich eine zentrale Standortauskunft/Übersicht?",
      a: "Das Geothermieportal Brandenburg bietet eine Standortabfrage u. a. für Bodengeothermie und oberflächennahe Geothermie (inkl. flächenbezogener Aspekte für Kollektoren).",
      links: [
        {
          label: "Geothermieportal Brandenburg (FAQ/PDF)",
          href: "https://geo.brandenburg.de/karten/htdocs/FAQ_GthPortal2021.pdf",
        },
      ],
    },
  ],

  cta: {
    title: "Brandenburg-Standort prüfen (datenbasiert)",
    text:
      "Du bekommst eine standortbezogene Vorabschätzung: Untergrundparameter (soweit verfügbar), erste Dimensionierung, Kosten-/Förderlogik und typische Genehmigungshinweise.",
    primary: {
      label: "Jetzt Standort-Report erstellen",
      href: "/report",
      title: "Erdwärme Standort-Report für Brandenburg erstellen",
    },
    secondary: { label: "Erst Grundlagen ansehen", href: "/flache-geothermie" },
    footnote:
      "Tipp: Wenn du Schutzgebietsstatus und grobe Heizleistung kennst, lässt sich die Machbarkeit deutlich schneller einordnen.",
  },
};
